import { useCallback, useEffect, useState } from "react";
import { obtenerTiposDocumentoLegal } from "../../../services/usuarios_tienda/informacion_tienda/ObtenerInformacionTienda";
import {
  obtenerHistorialContenidoLegal,
  crearContenidoLegalSetup,
  actualizarContenidoLegalSetup,
} from "../../../services/usuarios_tienda/tiendaSetup";
import "./TabContenidoLegal.css";
import LegalRichEditor from "../../../components/LegalRichEditor";

const emptyForm = () => ({
  titulo: "",
  contenido: "",
  version: "1.0",
  vigente: 1,
  publicado: 0,
});

function SetupTabContenidoLegal() {
  const [tipos, setTipos] = useState([]);
  const [selectedTipoId, setSelectedTipoId] = useState("");
  const [historial, setHistorial] = useState([]);
  const [loadingTipos, setLoadingTipos] = useState(true);
  const [loadingHist, setLoadingHist] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const loadTipos = useCallback(async () => {
    setLoadingTipos(true);
    setMsg(null);
    try {
      const data = await obtenerTiposDocumentoLegal();
      setTipos(Array.isArray(data) ? data : []);
    } catch (e) {
      setMsg({
        type: "err",
        text: e?.message || "No se pudieron cargar los tipos de documento.",
      });
      setTipos([]);
    }
    setLoadingTipos(false);
  }, []);

  const loadHistorial = useCallback(async (tipoId) => {
    if (!tipoId) {
      setHistorial([]);
      return [];
    }

    setLoadingHist(true);
    setMsg(null);

    const res = await obtenerHistorialContenidoLegal(tipoId);
    let list = [];

    if (res.ok) {
      list = Array.isArray(res.data) ? res.data : [];
      setHistorial(list);
    } else {
      setMsg({ type: "err", text: res.message });
      setHistorial([]);
    }

    setLoadingHist(false);
    return list;
  }, []);

  useEffect(() => {
    loadTipos();
  }, [loadTipos]);

  useEffect(() => {
    if (!selectedTipoId) {
      setHistorial([]);
      setSelectedDocId(null);
      setForm(emptyForm());
      return;
    }
    loadHistorial(selectedTipoId);
  }, [selectedTipoId, loadHistorial]);

  const patchForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const elegirNuevaVersion = () => {
    setSelectedDocId(null);
    setForm(emptyForm());
    setMsg(null);
  };

  const elegirDocumento = (row) => {
    setSelectedDocId(row.tienda_contenido_legal_id);
    setForm({
      titulo: row.titulo || "",
      contenido: row.contenido || "",
      version: row.version || "",
      vigente: Number(row.vigente) ? 1 : 0,
      publicado: Number(row.publicado) ? 1 : 0,
    });
    setMsg(null);
  };

  const guardar = async () => {
    const titulo = String(form.titulo || "").trim();

    if (!titulo) {
      setMsg({ type: "err", text: "El título es obligatorio." });
      return;
    }

    if (!selectedTipoId) {
      setMsg({ type: "err", text: "Selecciona un tipo de documento." });
      return;
    }

    setSaving(true);
    setMsg(null);

    if (selectedDocId) {
      const res = await actualizarContenidoLegalSetup({
        tienda_contenido_legal_id: selectedDocId,
        titulo,
        contenido: form.contenido,
        version: String(form.version || "").trim() || "1.0",
        vigente: form.vigente,
        publicado: form.publicado,
      });

      if (res.ok) {
        setMsg({ type: "ok", text: res.message || "Guardado correctamente." });
        await loadHistorial(selectedTipoId);
      } else {
        setMsg({ type: "err", text: res.message });
      }
    } else {
      const res = await crearContenidoLegalSetup({
        tipo_documento_id: Number(selectedTipoId),
        titulo,
        contenido: form.contenido,
        version: String(form.version || "").trim() || "1.0",
        vigente: form.vigente,
        publicado: form.publicado,
      });

      if (res.ok) {
        setMsg({ type: "ok", text: res.message || "Documento creado." });
        const newId = res.data?.tienda_contenido_legal_id;
        const list = await loadHistorial(selectedTipoId);

        if (newId && Array.isArray(list)) {
          const row = list.find((r) => r.tienda_contenido_legal_id === newId);
          if (row) elegirDocumento(row);
        }
      } else {
        setMsg({ type: "err", text: res.message });
      }
    }

    setSaving(false);
  };

  if (loadingTipos) {
    return <div className="legal-doc-loading">Cargando…</div>;
  }

  if (!tipos.length) {
    return (
      <div className="legal-doc-empty">
        No hay tipos de documento legal configurados. Crea registros en
        tipo_documento_legal para continuar.
      </div>
    );
  }

  return (
    <div className="legal-doc-page">
      {!selectedTipoId ? (
        <div className="legal-doc-initial-shell">
          <div className="legal-doc-initial-card">
            <div className="legal-doc-initial-top">
              <div className="legal-doc-topbar-group">
                <label className="legal-doc-label">Tipo de documento</label>
                <select
                  className="legal-doc-select"
                  value={selectedTipoId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedTipoId(v ? Number(v) : "");
                    setSelectedDocId(null);
                    setForm(emptyForm());
                  }}
                >
                  <option value="">Seleccionar…</option>
                  {tipos.map((t) => (
                    <option
                      key={t.tipo_documento_legal_id}
                      value={t.tipo_documento_legal_id}
                    >
                      {t.Nombre || t.codigo || `#${t.tipo_documento_legal_id}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
  
            <div className="legal-doc-empty-state clean">
              <h2>Selecciona un documento para comenzar</h2>
              <p>
                Elige el tipo de documento legal que deseas redactar o administrar.
              </p>
            </div>
          </div>
        </div>
      ) : loadingHist ? (
        <div className="legal-doc-loading-card">Cargando historial…</div>
      ) : (
        <div className="legal-doc-layout legal-doc-layout--focus">
          <aside className="legal-doc-sidebar compact">
            <div className="legal-doc-sidebar-block">
              <label className="legal-doc-label">Tipo de documento</label>
              <select
                className="legal-doc-select"
                value={selectedTipoId}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedTipoId(v ? Number(v) : "");
                  setSelectedDocId(null);
                  setForm(emptyForm());
                }}
              >
                <option value="">Seleccionar…</option>
                {tipos.map((t) => (
                  <option
                    key={t.tipo_documento_legal_id}
                    value={t.tipo_documento_legal_id}
                  >
                    {t.Nombre || t.codigo || `#${t.tipo_documento_legal_id}`}
                  </option>
                ))}
              </select>
            </div>
  
            <div className="legal-doc-sidebar-block">
              <button
                type="button"
                className="legal-doc-secondary-btn legal-doc-secondary-btn--full"
                onClick={elegirNuevaVersion}
                disabled={!selectedTipoId}
              >
                Nueva versión
              </button>
            </div>
  
            <div className="legal-doc-sidebar-block grow">
              <div className="legal-doc-sidebar-head simple">
                <h3>Versiones</h3>
                <span>{historial.length}</span>
              </div>
  
              {!historial.length ? (
                <div className="legal-doc-sidebar-empty">
                  No hay versiones todavía.
                </div>
              ) : (
                <div className="legal-doc-history-list">
                  {historial.map((row) => {
                    const active = selectedDocId === row.tienda_contenido_legal_id;
  
                    return (
                      <button
                        key={row.tienda_contenido_legal_id}
                        type="button"
                        className={`legal-doc-history-item ${active ? "active" : ""}`}
                        onClick={() => elegirDocumento(row)}
                      >
                        <div className="legal-doc-history-title">
                          v{row.version || "?"}
                        </div>
  
                        <div className="legal-doc-history-row">
                          <span>{Number(row.publicado) ? "Publicado" : "Borrador"}</span>
                          <span>{Number(row.vigente) ? "Vigente" : "Histórico"}</span>
                        </div>
  
                        {row.fecha_creado && (
                          <div className="legal-doc-history-date">
                            {new Date(row.fecha_creado).toLocaleString()}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </aside>
  
          <section className="legal-doc-workspace main-focus">
            <div className="legal-doc-workspace-top">
              <div className="legal-doc-header-grid legal-doc-header-grid--clean">
                <div className="legal-doc-field">
                  <label className="legal-doc-label">Título del documento</label>
                  <input
                    className="legal-doc-input legal-doc-title-input"
                    value={form.titulo}
                    onChange={(e) => patchForm("titulo", e.target.value)}
                    placeholder="Ej. Términos y condiciones de compra"
                  />
                </div>
  
                <div className="legal-doc-field legal-doc-version-field">
                  <label className="legal-doc-label">Versión</label>
                  <input
                    className="legal-doc-input"
                    value={form.version}
                    onChange={(e) => patchForm("version", e.target.value)}
                    placeholder="1.0"
                  />
                </div>
              </div>
  
              <div className="legal-doc-options-row">
                <label className="legal-doc-check clean">
                  <input
                    type="checkbox"
                    checked={!!Number(form.vigente)}
                    onChange={(e) => patchForm("vigente", e.target.checked ? 1 : 0)}
                  />
                  <span>Versión vigente</span>
                </label>
  
                <label className="legal-doc-check clean">
                  <input
                    type="checkbox"
                    checked={!!Number(form.publicado)}
                    onChange={(e) => patchForm("publicado", e.target.checked ? 1 : 0)}
                  />
                  <span>Publicado</span>
                </label>
              </div>
            </div>
  
            <div className="legal-doc-editor-area">
              <div className="legal-doc-editor-shell clean">
                <LegalRichEditor
                  key={`${selectedTipoId}-${selectedDocId ?? "nuevo"}`}
                  value={form.contenido}
                  onChange={(html) => patchForm("contenido", html)}
                  placeholder="Escribe aquí el contenido del documento..."
                />
              </div>
            </div>
  
            <div className="legal-doc-footer clean">
              <div className="legal-doc-footer-left">
                {msg && (
                  <p className={`legal-doc-message ${msg.type === "ok" ? "ok" : "err"}`}>
                    {msg.text}
                  </p>
                )}
              </div>
  
              <button
                type="button"
                className="legal-doc-primary-btn"
                disabled={saving}
                onClick={guardar}
              >
                {saving
                  ? "Guardando…"
                  : selectedDocId
                  ? "Guardar cambios"
                  : "Crear documento"}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

export default SetupTabContenidoLegal;